import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import { create as createFedopsLogger } from '@wix/fedops-logger';
import { App } from './components/App';
import { ErrorsProvider } from './providers/ErrorsProvider';
import { AppContextProvider } from './providers/AppContextProvider';
import { PremiumLocaleProvider } from '@wix/premium-react-text';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { UnknownChargeContextProvider } from './providers/UnknownChargeProvider';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChargeDetails } from './components/ChargeDetails/ChargeDetails';
import { Header } from './components/Header/Header';
import { getReferralInfo, obtainLanguage } from './utils';
import { createClientApi } from './hooks/useClientApi';
import { ClientApi } from './api/clientApi';
import i18n from './i18n';
import { Box, Loader, WixDesignSystemProvider } from '@wix/design-system';
import { BiLoggerProvider } from './providers/BiLoggerProvider';
import { initWixRecorder } from './services/WixRecorder';
import { ExperimentsBag } from '@wix/wix-experiments';
import { ExperimentsProvider } from '@wix/wix-experiments-react';

import '@wix/design-system/styles.global.css';
import { sanitizeCurrencySettingsValue } from './utils/currencyMojibake';

const fedopsLogger = createFedopsLogger('unknown-charge-app');

const {
  currencySettings: rawCurrencySettings,
  reCaptchaKey,
  isEditorX,
  isProduction,
  release,
} = window.__APP_PARAMS__;

// Defense-in-depth: re-run the same UTF-8-as-Latin-1 mojibake repair on the
// client. Older Express deployments and build-override deployments serve
// raw BAP data in window.__APP_PARAMS__.currencySettings, which can contain
// double-encoded affixes like "Â¤" (BRL placeholder) or "Â¥" (JPY symbol).
// See currencySettingsService.ts for details.
const currencySettings = Object.entries(rawCurrencySettings ?? {}).reduce(
  (acc: Record<string, any>, [code, value]: [string, any]) => {
    acc[code] = sanitizeCurrencySettingsValue(value);
    return acc;
  },
  {},
);

export interface IAppContext extends IAppParams {
  referalInfo: string;
  baseurl: string;
  language: string;
  locale: string;
  experiments: ExperimentsBag;
}

const cookieOrLoggedinLocale = obtainLanguage(window.__LOCALE__);

const appContext: IAppContext = {
  experiments: window.__EXPERIMENTS__,
  currencySettings,
  language: cookieOrLoggedinLocale,
  locale: cookieOrLoggedinLocale,
  reCaptchaKey,
  isEditorX,
  isProduction,
  baseurl: window.__BASEURL__,
  release,
  referalInfo: getReferralInfo(),
};

initWixRecorder(appContext, {});
fedopsLogger.appLoaded();
createClientApi(new ClientApi());

ReactDOM.render(
  <WixDesignSystemProvider features={{ newColorsBranding: true }}>
    <Suspense
      fallback={
        <Box width="100%" height="100vh" verticalAlign="middle" align="center">
          <Loader size="large" text="LOADING..." />
        </Box>
      }
    >
      <ErrorsProvider appContext={appContext}>
        <AppContextProvider values={appContext}>
          <ExperimentsProvider options={{ experiments: appContext.experiments }}>
          <BiLoggerProvider appContext={appContext}>
            <PremiumLocaleProvider currencySettings={currencySettings}>
              <GoogleReCaptchaProvider
                reCaptchaKey={reCaptchaKey}
                language={cookieOrLoggedinLocale}
              >
                <UnknownChargeContextProvider>
                  <I18nextProvider i18n={i18n(cookieOrLoggedinLocale)}>
                    <Header />
                    <Router>
                      <Routes>
                        <Route path="*" element={<App />} />
                        <Route
                          path="/billing/unknown-charge/charge-details"
                          element={<ChargeDetails />}
                        />
                      </Routes>
                    </Router>
                  </I18nextProvider>
                </UnknownChargeContextProvider>
              </GoogleReCaptchaProvider>
            </PremiumLocaleProvider>
          </BiLoggerProvider>
          </ExperimentsProvider>
        </AppContextProvider>
      </ErrorsProvider>
    </Suspense>
  </WixDesignSystemProvider>,
  document.getElementById('root')
);
